import { useCallback, useRef } from 'react';
import type React from 'react';
import { approveItem, createApproval, editApproval } from '../services/MendsVisionService';
import type { ApprovalDto } from '../services/MendsVisionService';
import { describeCreateOutcome } from './createApprovalOutcome';
import { readApiError } from './approvalContent';
import {
  DraftCardState,
  DraftCarrier,
  DRAFT_SUGGESTED_ACTION,
  QUEUED_MESSAGE,
  approveFailurePatch,
  approveResultPatch,
  createFailureMessage,
  discardedState,
  draftApprovalPayload,
  draftApprovalTitle,
} from './mindDraft';

export const NO_SECTOR_MESSAGE = 'Selecione uma conexão no menu primeiro.';
export const EDIT_FAILED_MESSAGE = 'Não consegui salvar a edição na fila. Nada foi enviado.';

type Message = DraftCarrier & { id: string };

/**
 * A aprovação devolvida pelo create já tem o conteúdo atual do card? Compara o corpo e, no payload, `text` e `pdf`
 * (igualdade exata; payload ausente ou ilegível conta como diferente, e aí o card edita).
 */
function approvalMatches(approval: ApprovalDto, text: string, actionPayload: string): boolean {
  if (approval.body !== text) return false;
  try {
    const stored = JSON.parse(approval.actionPayload ?? '');
    const wanted = JSON.parse(actionPayload);
    return stored?.text === wanted.text && stored?.pdf === wanted.pdf;
  } catch {
    return false;
  }
}

type EnsureResult =
  | { kind: 'queued'; approvalId: number }
  | { kind: 'final'; patch: Partial<DraftCardState> };

/**
 * Ações do card de rascunho do chat: Fila, Enviar agora, Editar e Descartar.
 * Nunca navega e nunca envia sem o clique: "Enviar agora" é o Ok do vendedor (D5), passando pelo
 * Aprovar do backend (revalidação). Um card tem no máximo uma operação em voo (duplo clique = uma chamada).
 */
export function useMindDrafts<M extends Message>(opts: {
  sectorId: number | null;
  messages: M[];
  setMessages: React.Dispatch<React.SetStateAction<M[]>>;
  /** Pede para salvar a sessão com o estado atual (status do card mudou). */
  requestPersist: () => void;
}) {
  const { sectorId, setMessages, requestPersist } = opts;
  const messagesRef = useRef(opts.messages);
  messagesRef.current = opts.messages;
  const inFlight = useRef(new Set<string>());

  const update = useCallback((msgId: string, fn: (s: DraftCardState) => DraftCardState, persist = false) => {
    setMessages((prev) => prev.map((m) => (m.id === msgId && m.draft ? { ...m, draft: fn(m.draft) } : m)));
    if (persist) requestPersist();
  }, [setMessages, requestPersist]);

  const patch = useCallback((msgId: string, p: Partial<DraftCardState>, persist = false) => {
    update(msgId, (s) => ({ ...s, ...p }), persist);
  }, [update]);

  const current = (msgId: string) => messagesRef.current.find((m) => m.id === msgId)?.draft;

  /** Garante a aprovação do card: cria (uma vez) ou sincroniza o texto editado depois de criada. */
  const ensureApproval = useCallback(async (sector: number, state: DraftCardState): Promise<EnsureResult> => {
    const { draft } = state;
    const text = draft.text ?? '';
    if (state.approvalId) {
      if (state.unsynced) {
        try {
          const { actionPayload } = draftApprovalPayload(draft, true, state.includePdf !== false);
          await editApproval(sector, state.approvalId, text, undefined, actionPayload);
        } catch (e) {
          return { kind: 'final', patch: { status: 'error', message: readApiError(e).message ?? EDIT_FAILED_MESSAGE } };
        }
      }
      return { kind: 'queued', approvalId: state.approvalId };
    }
    const { actionPayload, textSource } = draftApprovalPayload(draft, !!state.edited, state.includePdf !== false);
    try {
      const created = await createApproval({
        sectorId: sector,
        contactId: draft.contactId,
        contactName: (draft.contactName ?? '').trim() || null,
        title: draftApprovalTitle(draft),
        body: text,
        suggestedAction: DRAFT_SUGGESTED_ACTION,
        actionKind: 'send_message',
        actionPayload,
        textSource,
        idempotencyKey: state.idempotencyKey,
      });
      const outcome = describeCreateOutcome(created);
      if (outcome.kind === 'queued') {
        const { approval } = outcome;
        // Hit idempotente (resposta de um create anterior perdida): a aprovação existente pode ter o texto/PDF de
        // antes da edição. Sincroniza ANTES de devolver, para o Aprovar nunca mandar o conteúdo antigo.
        if (!approvalMatches(approval, text, actionPayload)) {
          try {
            await editApproval(sector, approval.id, text, undefined, actionPayload);
          } catch (e) {
            return {
              kind: 'final',
              patch: {
                status: 'error',
                message: readApiError(e).message ?? EDIT_FAILED_MESSAGE,
                approvalId: approval.id,
                unsynced: true,
              },
            };
          }
        }
        return { kind: 'queued', approvalId: approval.id };
      }
      // executed (rotina executou na hora) ou nada criado (sombra, …): nunca chama o Aprovar.
      return {
        kind: 'final',
        patch: { status: outcome.kind === 'executed' ? 'sent' : 'error', message: outcome.message },
      };
    } catch (e) {
      return { kind: 'final', patch: { status: 'error', message: createFailureMessage(e) } };
    }
  }, []);

  /** Reserva o card para uma operação; false quando já há uma em voo ou o card não aceita ação. */
  const begin = (msgId: string): DraftCardState | null => {
    const state = current(msgId);
    if (!state || inFlight.current.has(msgId)) return null;
    if (state.status !== 'draft' && state.status !== 'editing' && state.status !== 'error') return null;
    if (!(state.draft.text ?? '').trim()) return null;
    if (!sectorId) {
      patch(msgId, { status: 'error', message: NO_SECTOR_MESSAGE });
      return null;
    }
    inFlight.current.add(msgId);
    return state;
  };

  const queue = useCallback(async (msgId: string) => {
    const state = begin(msgId);
    if (!state || !sectorId) return;
    patch(msgId, { status: 'sending', message: undefined });
    try {
      const r = await ensureApproval(sectorId, state);
      if (r.kind === 'queued') {
        patch(msgId, { status: 'queued', message: QUEUED_MESSAGE, approvalId: r.approvalId, unsynced: undefined }, true);
      } else {
        patch(msgId, r.patch, true);
      }
    } finally {
      inFlight.current.delete(msgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, ensureApproval, patch]);

  const sendNow = useCallback(async (msgId: string) => {
    const state = begin(msgId);
    if (!state || !sectorId) return;
    // Salva já o "enviando": se a página recarregar no meio, o card volta como erro com aviso.
    patch(msgId, { status: 'sending', message: undefined }, true);
    try {
      const r = await ensureApproval(sectorId, state);
      if (r.kind !== 'queued') {
        patch(msgId, r.patch, true);
        return;
      }
      patch(msgId, { approvalId: r.approvalId, unsynced: undefined }, true);
      try {
        const result = await approveItem(sectorId, r.approvalId);
        patch(msgId, approveResultPatch(result), true);
      } catch (e) {
        // SUPERSEDED: terminal, card descartado. CONTEXT_UNVERIFIED e outros: erro, pode tentar de novo.
        patch(msgId, approveFailurePatch(e), true);
      }
    } finally {
      inFlight.current.delete(msgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, ensureApproval, patch]);

  const toggleEdit = useCallback((msgId: string) => {
    if (inFlight.current.has(msgId)) return;
    update(msgId, (s) => {
      if (s.status === 'editing') return { ...s, status: 'draft' };
      if (s.status === 'draft' || s.status === 'error') return { ...s, status: 'editing' };
      return s;
    });
  }, [update]);

  const changeText = useCallback((msgId: string, text: string) => {
    if (inFlight.current.has(msgId)) return;
    update(msgId, (s) => (s.status !== 'editing' ? s : {
      ...s,
      draft: { ...s.draft, text },
      edited: true,
      unsynced: s.approvalId ? true : s.unsynced,
    }));
  }, [update]);

  /** Caixa "Enviar o PDF junto": muda o card (salvo); com aprovação já criada, o próximo envio edita o payload. */
  const togglePdf = useCallback((msgId: string) => {
    if (inFlight.current.has(msgId)) return;
    update(msgId, (s) => (s.status !== 'draft' && s.status !== 'editing' && s.status !== 'error' ? s : {
      ...s,
      includePdf: s.includePdf === false,
      unsynced: s.approvalId ? true : s.unsynced,
    }), true);
  }, [update]);

  const discard = useCallback((msgId: string) => {
    if (inFlight.current.has(msgId)) return;
    update(msgId, (s) => (s.status === 'draft' || s.status === 'editing' || s.status === 'error' ? discardedState(s) : s), true);
  }, [update]);

  return { queue, sendNow, toggleEdit, changeText, togglePdf, discard };
}
